import React, { useState, useEffect } from 'react';
import Field from '../OutboundCampaign/TrustHub/Field';
import DropdownField from '../OutboundCampaign/TrustHub/DropdownField'
import RadioGroupField from '../OutboundCampaign/TrustHub/RadioGroupField'

import { connect } from "react-redux";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsStates } from '../../../store/usStatesSlice';
import { Segment, Form, Message } from 'semantic-ui-react';
import { setPageTitle, toggleNavigation, setMessage, setBreadCrum, toggleBreadcrum } from "../../../store/Page";
import api from '../../../api';
import './TrustHubComplianceForm.css';
import GlobalMessage from './GlobalMessage';

const TrustHubComplianceForm = (props) => {
  const dispatch = useDispatch();
  const { list, error } = useSelector((state) => state.usStates);
  useEffect(() => {
    if (!list.length) dispatch(fetchUsStates());
  }, [dispatch, list.length]);

  const { setPageTitle, toggleNavigation, toggleBreadcrum, user } = props;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set([1]));
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: General Information
    id: '',
    legalBusinessName: '',
    profileFriendlyName: '',
    country: 'US',
    addressStreet1: '',
    addressStreet2: '',
    addressCity: '',
    addressState: '',
    addressPostalCode: '',

    // Step 2: Business Information
    businessIdentity: 'direct_customer',
    businessType: '',
    businessIndustry: '',
    businessRegistrationIdType: 'EIN',
    businessRegistrationNumber: '',
    businessRegions: 'USA_AND_CANADA',
    businessWebsite: '',

    // Step 3: Authorized Representatives
    representatives: [{
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      businessTitle: '',
      jobPosition: ''
    }],

    // Step 4: Notification Settings
    notificationEmail: '',
    statusCallbackWebhook: '',

    // Step 5: Terms and Conditions
    termsAccepted: false,
    user_id: user.user_id
  });



  const checkExistingProfile = async () => {
    setLoading(true); // start loading
    try {
      const response = await api.trustHub.getProfile(user.user_id);
      const profile = response.data?.profile;

      if (profile) {
        // Map API representatives to form-friendly structure
        setIsEditMode(true);
        const mappedReps = profile.representatives?.map(rep => ({
          id: rep.id, // use API id as key
          firstName: rep.first_name || '',
          lastName: rep.last_name || '',
          email: rep.email || '',
          phoneNumber: formatPhoneNumber(rep.phone_number) || '',
          businessTitle: rep.business_title || '',
          jobPosition: rep.job_position || '',
        })) || [];

        // Pre-fill form
        setFormData(prev => ({
          ...prev,
          id: profile.id,
          legalBusinessName: profile.legal_business_name || '',
          profileFriendlyName: profile.profile_friendly_name || '',
          country: profile.country || 'US',
          addressStreet1: profile.address_street1 || '',
          addressStreet2: profile.address_street2 || '',
          addressCity: profile.address_city || '',
          addressState: profile.address_state || '',
          addressPostalCode: profile.address_postal_code || '',
          businessIdentity: profile.business_identity || 'direct_customer',
          businessType: profile.business_type || '',
          businessIndustry: profile.business_industry || '',
          businessRegistrationIdType: profile.business_registration_id_type || 'EIN',
          businessRegistrationNumber: profile.business_registration_number || '',
          businessRegions: profile.business_regions || 'USA_AND_CANADA',
          businessWebsite: profile.business_website || '',
          representatives: mappedReps.length ? mappedReps : prev.representatives,
          notificationEmail: profile.notification_email || '',
          statusCallbackWebhook: profile.status_callback_webhook || '',
          termsAccepted: profile.terms_accepted || false,
          user_id: profile.user_id || user.user_id,
        }));

        // Redirect if approved
        if (profile.status === 'twilio-approved') {
          props.history.push('/outbound-campaign');
        }
      }
    } catch (err) {
      console.log("No existing profile found or error:", err);
    } finally {
      setLoading(false);
    }
  };


  const [errors, setErrors] = useState({});

  useEffect(() => {
    setPageTitle({ title: "Trust Hub Compliance", subHeaderTitle: 'Customer Profile Setup' });
    //toggleNavigation({ toggle: true });
    //toggleBreadcrum({ toggle: false });
    checkExistingProfile();
  }, []);


  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  // Check if a step can be accessed
  const canAccessStep = (stepNumber) => {
    return completedSteps.has(stepNumber) || stepNumber === currentStep;
  };

  // Mark a step as completed
  const markStepCompleted = (stepNumber) => {
    setCompletedSteps(prev => new Set([...prev, stepNumber]));
  };

  const businessTypes = [
    { key: 'sole_proprietorship', text: 'Sole Proprietorship', value: 'Sole Proprietorship' },
    { key: 'partnership', text: 'Partnership', value: 'Partnership' },
    { key: 'llc', text: 'Limited Liability Corporation', value: 'Limited Liability Corporation' },
    { key: 'cooperative', text: 'Co-operative', value: 'Co-operative' },
    { key: 'nonprofit', text: 'Non-profit Corporation', value: 'Non-profit Corporation' },
    { key: 'corporation', text: 'Corporation', value: 'Corporation' }
  ];

  const businessIndustries = [
    { key: 'automotive', text: 'Automotive', value: 'AUTOMOTIVE' },
    { key: 'agriculture', text: 'Agriculture', value: 'AGRICULTURE' },
    { key: 'banking', text: 'Banking', value: 'BANKING' },
    { key: 'consumer', text: 'Consumer', value: 'CONSUMER' },
    { key: 'education', text: 'Education', value: 'EDUCATION' },
    { key: 'engineering', text: 'Engineering', value: 'ENGINEERING' },
    { key: 'energy', text: 'Energy', value: 'ENERGY' },
    { key: 'oil_gas', text: 'Oil and Gas', value: 'OIL_AND_GAS' },
    { key: 'fmcg', text: 'Fast Moving Consumer Goods', value: 'FAST_MOVING_CONSUMER_GOODS' },
    { key: 'financial', text: 'Financial', value: 'FINANCIAL' },
    { key: 'fintech', text: 'Fintech', value: 'FINTECH' },
    { key: 'food_beverage', text: 'Food and Beverage', value: 'FOOD_AND_BEVERAGE' },
    { key: 'government', text: 'Government', value: 'GOVERNMENT' },
    { key: 'healthcare', text: 'Healthcare', value: 'HEALTHCARE' },
    { key: 'hospitality', text: 'Hospitality', value: 'HOSPITALITY' },
    { key: 'insurance', text: 'Insurance', value: 'INSURANCE' },
    { key: 'legal', text: 'Legal', value: 'LEGAL' },
    { key: 'manufacturing', text: 'Manufacturing', value: 'MANUFACTURING' },
    { key: 'media', text: 'Media', value: 'MEDIA' },
    { key: 'online', text: 'Online', value: 'ONLINE' },
    { key: 'raw_materials', text: 'Raw Materials', value: 'RAW_MATERIALS' },
    { key: 'real_estate', text: 'Real Estate', value: 'REAL_ESTATE' },
    { key: 'religion', text: 'Religion', value: 'RELIGION' },
    { key: 'retail', text: 'Retail', value: 'RETAIL' },
    { key: 'jewelry', text: 'Jewelry', value: 'JEWELRY' },
    { key: 'technology', text: 'Technology', value: 'TECHNOLOGY' },
    { key: 'telecommunications', text: 'Telecommunications', value: 'TELECOMMUNICATIONS' },
    { key: 'transportation', text: 'Transportation', value: 'TRANSPORTATION' },
    { key: 'travel', text: 'Travel', value: 'TRAVEL' },
    { key: 'electronics', text: 'Electronics', value: 'ELECTRONICS' },
    { key: 'not_for_profit', text: 'Not For Profit', value: 'NOT_FOR_PROFIT' }
  ];

  const businessRegistrationTypes = [
    { key: 'ein', text: 'Employer Identification Number (EIN)', value: 'EIN' }
  ];

  const businessRegions = [
    { key: 'africa', text: 'Africa', value: 'AFRICA' },
    { key: 'asia', text: 'Asia', value: 'ASIA' },
    { key: 'australia', text: 'Australia', value: 'AUSTRALIA' },
    { key: 'europe', text: 'Europe', value: 'EUROPE' },
    { key: 'latin_america', text: 'Latin America', value: 'LATIN_AMERICA' },
    { key: 'usa_canada', text: 'USA and Canada', value: 'USA_AND_CANADA' }
  ];

  const countries = [
    { key: 'US', text: 'United States', value: 'US' }
  ];

  // Validation functions
  const validateEIN = (ein) => {
    // EIN format: XX-XXXXXXX (2 digits, hyphen, 7 digits)
    const einRegex = /^\d{2}-\d{7}$/;
    return einRegex.test(ein);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const validateWebsite = (website) => {
    if (!website) return true; // Website is optional
    const websiteRegex = /^https?:\/\/.+\..+/;
    return websiteRegex.test(website);
  };

  // Formatting functions
  const formatEIN = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as XX-XXXXXXX
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 9) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    } else {
      return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // If starts with 1, remove it for US formatting
    const cleanDigits = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;

    // Format as (XXX) XXX-XXXX
    if (cleanDigits.length <= 3) {
      return cleanDigits;
    } else if (cleanDigits.length <= 6) {
      return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3)}`;
    } else if (cleanDigits.length <= 10) {
      return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6)}`;
    } else {
      return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6, 10)}`;
    }
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;

    // Apply formatting for specific fields
    if (field === 'businessRegistrationNumber') {
      formattedValue = formatEIN(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const addSecondRepresentative = () => {
    setFormData(prev => ({
      ...prev,
      representatives: [...prev.representatives, {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        businessTitle: '',
        jobPosition: ''
      }]
    }));
  };

  const removeSecondRepresentative = () => {
    setFormData(prev => ({
      ...prev,
      representatives: [prev.representatives[0]] // Keep only the first representative
    }));
  };

  const handleRepresentativeChange = (index, field, value) => {
    let formattedValue = value;

    // Apply formatting for phone number field
    if (field === 'phoneNumber') {
      formattedValue = formatPhoneNumber(value);
    }

    const newRepresentatives = [...formData.representatives];
    newRepresentatives[index][field] = formattedValue;
    setFormData(prev => ({
      ...prev,
      representatives: newRepresentatives
    }));

    // Clear error when user starts typing
    const errorKey = `rep_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: null
      }));
    }
  };

  const nameRegex = /^(?!\d+$)(?!.*[-'\s]{2})[\p{L}0-9][\p{L}0-9\s'\-\.]{1,48}[\p{L}0-9\.]$/u;


  const validateStep = (step) => {
    const newErrors = {};

    // --- Helper for length check ---
    const checkLength = (value, min, max, fieldName) => {
      if (value.trim().length < min) return `${fieldName} must be at least ${min} characters.`;
      if (value.trim().length > max) return `${fieldName} cannot exceed ${max} characters.`;
      return null;
    };

    // --- Helper for name validation ---
    const validateName = (value, fieldName, min = 2, max = 50) => {
      if (!value || value.trim() === '') return `${fieldName} is required`;
      const err = checkLength(value, min, max, fieldName);
      if (err) return err;
      if (!nameRegex.test(value.trim())) return `${fieldName} contains invalid characters`;
      return null;
    };

    switch (step) {
      // 🧩 STEP 1: Business Info
      case 1:
        // Legal business name
        if (!formData.legalBusinessName) {
          newErrors.legalBusinessName = 'Legal business name is required';
        } else {
          const err = validateName(formData.legalBusinessName, 'Legal business name', 3, 50);
          if (err) newErrors.legalBusinessName = err;
        }

        // Profile friendly name
        if (!formData.profileFriendlyName) {
          newErrors.profileFriendlyName = 'Profile friendly name is required';
        } else {
          const err = validateName(formData.profileFriendlyName, 'Profile friendly name', 3, 50);
          if (err) newErrors.profileFriendlyName = err;
        }

        // Country
        if (!formData.country) newErrors.country = 'Country is required';

        // Street
        if (!formData.addressStreet1) {
          newErrors.addressStreet1 = 'Street address is required';
        } else {
          const err = checkLength(formData.addressStreet1, 3, 100, 'Street address');
          if (err) newErrors.addressStreet1 = err;
        }

        // City
        if (!formData.addressCity) {
          newErrors.addressCity = 'City is required';
        } else {
          const err = validateName(formData.addressCity, 'City', 2, 50);
          if (err) newErrors.addressCity = err;
        }

        // State
        if (!formData.addressState) {
          newErrors.addressState = 'State/Province is required';
        }

        // Postal Code
        if (!formData.addressPostalCode) {
          newErrors.addressPostalCode = 'Postal code is required';
        } else if (!/^\d{5,6}$/.test(formData.addressPostalCode)) {
          newErrors.addressPostalCode = 'Postal code must be 5–6 digits';
        }
        break;

      // 🧩 STEP 2: Business Details
      case 2:
        if (!formData.businessIdentity) newErrors.businessIdentity = 'Business identity is required';
        if (!formData.businessType) newErrors.businessType = 'Business type is required';
        if (!formData.businessIndustry) newErrors.businessIndustry = 'Business industry is required';
        if (!formData.businessRegistrationIdType) newErrors.businessRegistrationIdType = 'Business registration ID type is required';

        if (!formData.businessRegistrationNumber) {
          newErrors.businessRegistrationNumber = 'Business registration number is required';
        } else if (!validateEIN(formData.businessRegistrationNumber)) {
          newErrors.businessRegistrationNumber = 'EIN must be in format XX-XXXXXXX (e.g., 12-3456789)';
        }

        if (!formData.businessWebsite) {
          newErrors.businessWebsite = 'Business website is required';
        } else if (!validateWebsite(formData.businessWebsite)) {
          newErrors.businessWebsite = 'Website must be a valid URL (e.g., https://example.com)';
        }
        break;

      // 🧩 STEP 3: Representatives
      case 3:
        if (formData.representatives.length < 1) {
          newErrors.representatives = 'At least 1 authorized representative is required';
        }

        formData.representatives.forEach((rep, index) => {
          // First Name
          const firstNameErr = validateName(rep.firstName, 'First name');
          if (firstNameErr) newErrors[`rep_${index}_firstName`] = firstNameErr;

          // Last Name
          const lastNameErr = validateName(rep.lastName, 'Last name');
          if (lastNameErr) newErrors[`rep_${index}_lastName`] = lastNameErr;

          // Email
          if (!rep.email) newErrors[`rep_${index}_email`] = 'Email is required';
          else if (!validateEmail(rep.email))
            newErrors[`rep_${index}_email`] = 'Please enter a valid email address';

          // Phone
          if (!rep.phoneNumber) newErrors[`rep_${index}_phoneNumber`] = 'Phone number is required';
          else if (!validatePhoneNumber(rep.phoneNumber))
            newErrors[`rep_${index}_phoneNumber`] =
              'Phone number must contain exactly 10 digits.';

          // Business Title
          const businessTitleErr = validateName(rep.businessTitle, 'Business title');
          if (businessTitleErr) newErrors[`rep_${index}_businessTitle`] = businessTitleErr;
        });
        break;

      // 🧩 STEP 4: Notification Email
      case 4:
        if (!formData.notificationEmail) newErrors.notificationEmail = 'Notification email is required';
        else if (!validateEmail(formData.notificationEmail))
          newErrors.notificationEmail = 'Please enter a valid email address';
        break;

      // 🧩 STEP 5: Terms
      case 5:
        if (!formData.termsAccepted) newErrors.termsAccepted = 'You must accept the terms of service';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleNext = () => {
    if (validateStep(currentStep)) {
      markStepCompleted(currentStep);
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    setLoading(true);

    try {
      // Always use createProfile API (backend uses updateOrCreate)
      const response = await api.trustHub.createProfile(formData);

      // Check if response is successful
      if (response.data.success) {
        let text = '';
        let type = 'success';

        // Handle draft/pending status (phone transfer queued for background processing)
        if (response.data.status === 'draft') {
          text = response.data.message || 'Your profile is being processed. This may take 5-10 minutes. You will receive an email once completed.';
          type = 'info';

          dispatch(setMessage({ text, type }));

          // Notify parent to recheck profile status
          if (props.onProfileUpdated) {
            props.onProfileUpdated();
          }
        }
        // Handle immediate completion (with isCompliant field)
        else if (response.data.isCompliant !== undefined) {
          if (response.data.isCompliant) {
            if (response.data.profileStatus === 'pending-phone-transfer') {
              text = response.data.message || 'Your phone number transfer is still in progress. Please wait a few minutes and try submitting again.';
              type = 'warning';
            } else if (response.data.profile?.status === 'twilio-approved') {
              text = 'Trust Hub profile is approved!';
            } else {
              text = isEditMode
                ? 'Trust Hub profile updated successfully!'
                : 'Trust Hub profile submitted successfully! It will be reviewed by Twilio.';
            }
          } else {
            const submittedStatus = response.data.profileStatus;
            if (['pending-review', 'in-review', 'twilio-approved'].includes(submittedStatus)) {
              text = isEditMode
                ? 'Trust Hub profile updated and submitted for review!'
                : 'Trust Hub profile submitted for review!';
            } else {
              text = 'Profile submitted but compliance issues were found. Please review the errors and resubmit.';
              type = 'error';
            }
          }

          dispatch(setMessage({ text, type }));

          // Notify parent to recheck profile status
          if (props.onProfileUpdated) {
            props.onProfileUpdated();
          }
        }
        // Fallback for other success responses
        else {
          text = response.data.message || 'Trust Hub profile submitted successfully!';
          dispatch(setMessage({ text, type }));

          if (props.onProfileUpdated) {
            props.onProfileUpdated();
          }
        }
      } else {
        // Handle error response
        dispatch(setMessage({
          text: response?.data?.message || "Validation failed. Please check details.",
          type: "error",
          errors: response?.data?.errors || null
        }));
      }
    } catch (error) {
      console.error(error);
      dispatch(setMessage({
        text: error?.response?.data?.message || "An error occurred while submitting your profile.",
        type: "error"
      }));
    } finally {
      setLoading(false);
    }
  };



  const renderStep1 = () => (
    <div className="trust-hub-step">
      <h3>General Information</h3>
      <p>Provide your organization's basic information:</p>

      {/* Legal Business Name & Profile Friendly Name */}
      <div className="form-group" style={{ display: 'flex', gap: '20px', marginBottom: 0 }}>
        <div style={{ flex: 1 }}>
          <Field
            label="Legal Business Name"
            required
            name="legalBusinessName"
            value={formData.legalBusinessName}
            onChange={(e) => handleInputChange('legalBusinessName', e.target.value)}
            placeholder="Exact legal business name as registered"
            error={errors.legalBusinessName}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Field
            label="Profile Friendly Name"
            required
            name="profileFriendlyName"
            value={formData.profileFriendlyName}
            onChange={(e) => handleInputChange('profileFriendlyName', e.target.value)}
            placeholder="Short label for your organization"
            error={errors.profileFriendlyName}
          />
        </div>
      </div>

      {/* Country Dropdown */}

      <DropdownField
        label="Country"
        required
        name="country"
        value={formData.country}
        onChange={(e) => handleInputChange('country', e.target.value)}
        options={countries}
        error={errors.country}
        inputProps={{ readOnly: true }}
      />

      {/* Address Street 1 & 2 */}
      <div style={{ display: 'flex', gap: '20px' }}>
        <div style={{ flex: 1 }}>
          <Field
            label="Address Street 1"
            name="addressStreet1"
            required
            value={formData.addressStreet1}
            onChange={(e) => handleInputChange('addressStreet1', e.target.value)}
            placeholder="Physical street address"
            error={errors.addressStreet1}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Field
            label="Address Street 2"
            name="addressStreet2"
            value={formData.addressStreet2}
            onChange={(e) => handleInputChange('addressStreet2', e.target.value)}
            placeholder="Additional address line (optional)"
          />
        </div>
      </div>

      {/* City, State/Province, Postal Code */}
      <div style={{ display: 'flex', gap: '20px' }}>
        <div style={{ flex: 1 }}>
          <Field
            label="City"
            required
            name="addressCity"
            value={formData.addressCity}
            onChange={(e) => handleInputChange('addressCity', e.target.value)}
            placeholder="City"
            error={errors.addressCity}
          />
        </div>
        <div style={{ flex: 1 }}>
          <DropdownField
            label="State or Province"
            name="addressState"
            required
            value={formData.addressState}
            onChange={(e) => handleInputChange('addressState', e.target.value)}
            options={list.map(state => ({
              key: state.id,
              text: state.name,
              value: state.id,
            }))}
            placeholder="Select State"
            error={errors.addressState}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Field
            label="Postal Code"
            required
            name="addressPostalCode"
            value={formData.addressPostalCode}
            onChange={(e) => handleInputChange('addressPostalCode', e.target.value)}
            placeholder="Postal code"
            error={errors.addressPostalCode}
          />
        </div>
      </div>
    </div>
  );
  const renderStep2 = () => (
    <div className="trust-hub-step">
      <h3>Business Information</h3>
      <p>Provide details about your business:</p>

      {/* Business Type & Industry */}
      <div style={{ display: 'flex', gap: '20px' }}>
        <div style={{ flex: 1 }}>
          <DropdownField
            label="Business Type"
            options={businessTypes}
            required
            value={formData.businessType}
            onChange={(e, { value }) => handleInputChange('businessType', value)}
            placeholder="Select business type"
            error={errors.businessType}
          />
        </div>
        <div style={{ flex: 1 }}>
          <DropdownField
            label="Business Industry"
            hint="If you’re unsure, you’re likely a “consumer” business."
            options={businessIndustries}
            required
            value={formData.businessIndustry}
            onChange={(e, { value }) => handleInputChange('businessIndustry', value)}
            placeholder="Select industry"
            error={errors.businessIndustry}
          />
        </div>
      </div>

      {/* Registration ID & Number */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
          alignItems: 'start',
        }}
      >
        <DropdownField
          required
          label="Business Registration ID Type"
          options={businessRegistrationTypes}
          value={formData.businessRegistrationIdType}
          readOnly
          error={errors.businessRegistrationIdType}
        />

        <Field
          label="Business Registration Number"
          required
          value={formData.businessRegistrationNumber}
          onChange={(e) => handleInputChange('businessRegistrationNumber', e.target.value)}
          placeholder="12-3456789"
          error={errors.businessRegistrationNumber}
        />
      </div>


      {/* Regions & Website */}
      <div style={{ display: 'flex', gap: '20px' }}>
        <div style={{ flex: 1 }}>
          <Field
            label="Business Regions of Operations"
            required
            value={formData.businessRegions === 'USA_AND_CANADA' ? 'USA and Canada' : formData.businessRegions}
            readOnly
          />
        </div>
        <div style={{ flex: 1 }}>
          <Field
            label="Business Website"
            required
            value={formData.businessWebsite}
            onChange={(e) => handleInputChange('businessWebsite', e.target.value)}
            placeholder="https://your-website.com"
            error={errors.businessWebsite}
          />
        </div>
      </div>
    </div>
  );
  const renderStep3 = () => (
    <div className="trust-hub-step">
      <h3>Authorized Representatives</h3>
      <p>
        Provide information about authorized representatives. The first representative is required; the second is optional.
      </p>

      {formData.representatives.map((rep, index) => (
        <div key={index} style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4>
              Authorized Representative #{index + 1} {index === 1 ? '(Optional)' : ''}
            </h4>
            {index === 1 && (
              <button
                type="button"
                onClick={removeSecondRepresentative}
                className="btn linear-gradient yr-submitbtn btn-sm"
                style={{ fontSize: '12px', padding: '5px 10px' }}
              >
                Remove
              </button>
            )}
          </div>

          {/* First Name & Last Name */}
          <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              <Field
                required
                label="First Name"
                placeholder="First name"
                value={rep.firstName}
                onChange={(e) => handleRepresentativeChange(index, 'firstName', e.target.value)}
                error={errors[`rep_${index}_firstName`]}
              />
            </div>

            <div style={{ flex: 1 }}>
              <Field
                required
                label="Last Name"
                placeholder="Last name"
                value={rep.lastName}
                onChange={(e) => handleRepresentativeChange(index, 'lastName', e.target.value)}
                error={errors[`rep_${index}_lastName`]}
              />
            </div>
          </div>

          {/* Email & Phone */}
          <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              <Field
                required
                label="Email"
                type="email"
                placeholder="name@example.com"
                value={rep.email}
                onChange={(e) => handleRepresentativeChange(index, 'email', e.target.value)}
                error={errors[`rep_${index}_email`]}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Field
                required
                label="Phone Number"
                placeholder="10-digit phone number"
                value={rep.phoneNumber}
                onChange={(e) => handleRepresentativeChange(index, 'phoneNumber', e.target.value)}
                error={errors[`rep_${index}_phoneNumber`]}
              />
            </div>
          </div>

          {/* Business Title & Job Position */}
          <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              <Field
                required
                label="Business Title"
                placeholder="Business title or position (CEO, Manager)"
                value={rep.businessTitle}
                onChange={(e) => handleRepresentativeChange(index, 'businessTitle', e.target.value)}
                error={errors[`rep_${index}_businessTitle`]}
              />
            </div>
            <div style={{ flex: 1 }}>
              <DropdownField
                required
                label="Job Position"
                placeholder="Select job position"
                options={[
                  { key: 'director', text: 'Director', value: 'Director' },
                  { key: 'gm', text: 'GM', value: 'GM' },
                  { key: 'vp', text: 'VP', value: 'VP' },
                  { key: 'ceo', text: 'CEO', value: 'CEO' },
                  { key: 'cfo', text: 'CFO', value: 'CFO' },
                  { key: 'general_counsel', text: 'General Counsel', value: 'General Counsel' },
                  { key: 'other', text: 'Other', value: 'Other' },
                ]}
                value={rep.jobPosition}
                onChange={(e, { value }) => handleRepresentativeChange(index, 'jobPosition', value)}
                error={errors[`rep_${index}_jobPosition`]}
              />
            </div>

          </div>
        </div>
      ))}

      {formData.representatives.length === 1 && (
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={addSecondRepresentative}
            className="btn linear-gradient yr-submitbtn btn-sm"
            style={{ fontSize: '14px', padding: '8px 16px' }}
          >
            + Add Second Representative (Optional)
          </button>
        </div>
      )}
    </div>
  );



  const renderStep4 = () => (
    <div className="trust-hub-step">
      <h3>Notification Settings</h3>
      <p>Configure how you want to receive notifications about your Trust Hub profile:</p>

      {/* Notification Email */}
      <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
        <div style={{ flex: 1 }}>
          <Field
            required
            label="Email Address"
            placeholder="notifications@yourcompany.com"
            type="email"
            value={formData.notificationEmail}
            onChange={(e) => handleInputChange('notificationEmail', e.target.value)}
            error={errors.notificationEmail}
          />
        </div>
      </div>
    </div>
  );


const renderStep5 = () => (
  <div className="trust-hub-step">
    <h3>Terms of Service and Privacy Policy</h3>
    <p>
      Please review and accept the following terms before submitting your Trust Hub profile.
    </p>
    <div className="form-group" style={{ marginBottom: 10 }}>
      <div className="custom-control custom-checkbox">
        <input
          type="checkbox"
          className="custom-control-input"
          id="termsAccepted"
          checked={!!formData.termsAccepted}
          onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
          style={{ accentColor: 'rgb(15, 98, 254)' }}
        />
        <label
          className="custom-control-label"
          htmlFor="termsAccepted"
          style={{ color: '#495057', fontWeight: '500' }}
        >
          I agree to Twilio’s{" "}
          <a
            href="https://www.twilio.com/en-us/legal/tos"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#0f62fe', textDecoration: 'underline' }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://www.twilio.com/en-us/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#0f62fe', textDecoration: 'underline' }}
          >
            Privacy Policy
          </a>
          <span style={{ color: 'red' }}> *</span>
        </label>
      </div>

      {errors.termsAccepted && (
        <div
          className="invalid-feedback d-block"
          style={{
            color: '#dc3545',
            fontSize: '12px',
            marginTop: '5px',
          }}
        >
          {errors.termsAccepted}
        </div>
      )}
    </div>
  </div>
);


  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return renderStep1();
    }
  };

  const renderProfileStatusMessage = (status) => {
    let message = '';
    let color = '';

    switch (status) {
      case 'draft':
        message = 'Your profile setup is incomplete. Please complete all sections and submit.';
        color = 'yellow';
        return <Message color={color}>{message}</Message>;

      case 'pending-phone-transfer':
        message = 'Your phone number transfer is still in progress. Please wait a few minutes and try submitting again.';
        color = 'yellow';
        return <Message color={color}>{message}</Message>;

      case 'twilio-rejected':
        message = 'Your profile was rejected. Please update the information and resubmit.';
        color = 'red';
        return <Message color={color}>{message}</Message>;

      default:
        message = 'Before setting up an outbound calling campaign, it is required to complete this SHAKEN/STIR attestation. Please answer the following questions below:';
        return <p style={{ color: 'grey', marginBottom: '16px' }}>{message}</p>;
    }
  };

  // Usage in JSX
  return (
    <div className="kt-container kt-body kt-grid kt-grid--ver">
      <div className="yr-body-p" style={{ margin: '10px' }}>
        <div className="trust-hub-container">
          <div className="trust-hub-header">
            <h2>SHAKEN/STIR Business Profile</h2>
            <p>{renderProfileStatusMessage(props.profileStatus)}</p>
            <GlobalMessage />
          </div>
          {(props.profileStatus === 'draft' || props.profileStatus === 'twilio-rejected' || props.profileStatus === 'pending-phone-transfer' || !props.profileStatus) ? (
            <div className="trust-hub-content">
              <div className="trust-hub-sidebar">
                <div className="step-navigation">
                  <div
                    className={`step-item ${currentStep === 1 ? 'active' : completedSteps.has(1) ? 'completed' : ''} ${canAccessStep(1) ? 'clickable' : 'disabled'}`}
                    onClick={() => canAccessStep(1) && setCurrentStep(1)}
                  >
                    <div className="step-indicator">
                      {completedSteps.has(1) ? <i className="check icon"></i> : <span>1</span>}
                    </div>
                    <div className="step-content">
                      <div className="step-title">General Information</div>
                    </div>
                  </div>

                  <div
                    className={`step-item ${currentStep === 2 ? 'active' : completedSteps.has(2) ? 'completed' : ''} ${canAccessStep(2) ? 'clickable' : 'disabled'}`}
                    onClick={() => canAccessStep(2) && setCurrentStep(2)}
                  >
                    <div className="step-indicator">
                      {completedSteps.has(2) ? <i className="check icon"></i> : <span>2</span>}
                    </div>
                    <div className="step-content">
                      <div className="step-title">Business Information</div>
                    </div>
                  </div>

                  <div
                    className={`step-item ${currentStep === 3 ? 'active' : completedSteps.has(3) ? 'completed' : ''} ${canAccessStep(3) ? 'clickable' : 'disabled'}`}
                    onClick={() => canAccessStep(3) && setCurrentStep(3)}
                  >
                    <div className="step-indicator">
                      {completedSteps.has(3) ? <i className="check icon"></i> : <span>3</span>}
                    </div>
                    <div className="step-content">
                      <div className="step-title">Authorized Representatives</div>
                    </div>
                  </div>

                  <div
                    className={`step-item ${currentStep === 4 ? 'active' : completedSteps.has(4) ? 'completed' : ''} ${canAccessStep(4) ? 'clickable' : 'disabled'}`}
                    onClick={() => canAccessStep(4) && setCurrentStep(4)}
                  >
                    <div className="step-indicator">
                      {completedSteps.has(4) ? <i className="check icon"></i> : <span>4</span>}
                    </div>
                    <div className="step-content">
                      <div className="step-title">Notification Settings</div>
                    </div>
                  </div>

                  <div
                    className={`step-item ${currentStep === 5 ? 'active' : ''} ${canAccessStep(5) ? 'clickable' : 'disabled'}`}
                    onClick={() => canAccessStep(5) && setCurrentStep(5)}
                  >
                    <div className="step-indicator">
                      <span>5</span>
                    </div>
                    <div className="step-content">
                      <div className="step-title">Terms of Service</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="trust-hub-main">
                <Segment className="trust-hub-form-segment">
                  {loading && <div className="ui loading form"></div>}
                  <Form>
                    {renderCurrentStep()}
                  </Form>
                </Segment>
              </div>
            </div>
          ) : null}
          {(props.profileStatus === 'draft' || props.profileStatus === 'twilio-rejected' || props.profileStatus === 'pending-phone-transfer' || !props.profileStatus) ? (
            <div className="trust-hub-navigation">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                >
                  <span>Go Back</span>
                </button>
              )}

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={loading}
                  className="btn linear-gradient yr-submitbtn btn-sm"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn linear-gradient yr-submitbtn btn-sm"
                >
                  Submit
                </button>
              )}
            </div>

          ) : null}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user.company,
  };
};

export default connect(mapStateToProps, { setPageTitle, toggleNavigation, setMessage, setBreadCrum, toggleBreadcrum })(TrustHubComplianceForm);
